import { useMemo } from 'react'
import { useIntl } from 'intl'
import { constants } from 'helpers'
import { useAb, useFt } from 'hooks'
import cookieStorage from 'cookie-storage'

import messages from './messages'


type Data = {
  firstProducts?: { product: QueueModule.AddItemProduct }[]
  tax?: { label: string, amount: number }
  shipping?: { label: string, note: string, price: number }[]
  discounts?: { label: string, note: string, amount: number, unit: string, coupon?: { code: string } }[]
  credits?: { label: string, amount: number }[]
  subTotal: number
  total: number
  purchaseLevel?: PaymentModule.PurchaseLevel
  isAlwaysFreeMessageEnabled?: boolean
  isSubscriptionDiscountDisabled?: boolean
}

// formats bill info from cart / inquire / payment session
const useOrderBill = (data: Data): OrderItems.BillItem[] => {
  const { firstProducts, tax, shipping, discounts, credits, subTotal, total, purchaseLevel, isAlwaysFreeMessageEnabled, isSubscriptionDiscountDisabled } = data || {}

  const intl = useIntl()

  const isUpchargeInfoPaymentEnabled = useAb(constants.abTests.upchargeInfoPayment) === 'b'
  const isGiftSubscriptionOfferEnabled = useFt(constants.features.giftSubscriptionOffer)

  const isSubscriptionOnly = purchaseLevel === 'SUBSCRIPTION_ONLY'
  const isFreeTrial = cookieStorage.getItem(constants.cookieNames.isFreeTrialCoupon)

  return useMemo(() => {
    if (typeof total === 'undefined' || typeof subTotal === 'undefined') {
      return []
    }

    const discountItems = discounts
      ?.filter(({ coupon, label }) => {
        if (isGiftSubscriptionOfferEnabled && coupon?.code === 'NO_CHARGE') {
          return false
        }

        if (isSubscriptionDiscountDisabled && label.toLocaleLowerCase().includes('subscription')) {
          return false
        }

        return true
      })
      ?.map(({ label, note, amount, unit }) => ({
        label,
        note,
        value: unit && unit !== '$' ? `- ${amount}${unit}` : intl.formatPrice(amount * -1),
        dataTestId: 'discount',
      })) || []

    const creditsItems = credits?.map(({ label, amount }) => ({
      label,
      value: intl.formatPrice(amount * -1),
      dataTestId: 'credits',
    })) || []


    const shippingItems = shipping?.map(({ label, note, price }) => {
      const isAlwaysFreeMessage = (
        isAlwaysFreeMessageEnabled
        && !isFreeTrial
        && (label.match(/subscription/i) || isSubscriptionOnly)
      )
      const freeMessage = isAlwaysFreeMessage ? messages.alwaysFree : messages.free

      return {
        label,
        note,
        value: price ? intl.formatPrice(price) : freeMessage,
        dataTestId: 'shipping',
      }
    }) || []

    const surchargeItems = isUpchargeInfoPaymentEnabled && firstProducts?.map(({ product }): OrderItems.BillItem => {
      return {
        disabled: true,
        label: messages.surcharge,
        value: intl.formatPrice(product.upchargePrice),
        note: messages.surchargeNote,
        noteColor: 'gold-50',
      }
    }) || []

    const taxItems = tax ? [
      {
        label: tax.label,
        value: intl.formatPrice(tax.amount),
        dataTestId: 'tax',
      },
    ] : []

    // subtotal, discounts, credits, shipping, taxes, total
    return [
      {
        label: messages.subTotal,
        value: intl.formatPrice(subTotal),
        isSubTotal: true,
        dataTestId: 'subtotal',
      },
      ...discountItems,
      ...creditsItems,
      ...surchargeItems,
      ...shippingItems,
      ...taxItems,
      {
        label: messages.total,
        value: intl.formatPrice(total),
        isTotal: true,
        dataTestId: 'total',
      },
    ]
  }, [
    intl,
    credits,
    discounts,
    firstProducts,
    isAlwaysFreeMessageEnabled,
    isSubscriptionDiscountDisabled,
    isFreeTrial,
    isGiftSubscriptionOfferEnabled,
    isSubscriptionOnly,
    isUpchargeInfoPaymentEnabled,
    shipping,
    subTotal,
    tax,
    total,
  ])
}


export default useOrderBill
